import React, { useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { IFSGraphData, IFSGraphTotals } from "../../interfaces/IFSForecast.interfaces";

interface GraphProps {
  monthlySummaries: IFSGraphData[];
  totals: IFSGraphTotals | null;
}

const Graph: React.FC<GraphProps> = ({ monthlySummaries, totals, }) => {
  const [showDefiniteAfterTP, setShowDefiniteAfterTP] = useState(false);
  const [includeProposed, setIncludeProposed] = useState(false);
  // 🔹 Group data by month
  const chartData = monthlySummaries.reduce((acc: any[], curr) => {

    const month = curr.monthLabel || "Unknown";
    let existing = acc.find((d) => d.monthName === month);
    if (!existing) {
      existing = {
        monthName: month,
        definite: 0,
        proposed: 0,
        confidenceWeightedProposed: 0,
        thirdPartyCost: 0,
        definiteAfterThirdParty: 0
      };
      acc.push(existing);
    }

    if (curr.proposalType.toLowerCase() === "definite") {
      existing.definite += curr.totalForecast;
      existing.thirdPartyCost += curr.thirdPartyCost || 0;
      existing.definiteAfterThirdParty += curr.definiteAfterThirdParty || 0;
    } else if (curr.proposalType.toLowerCase() === "proposed") {
      existing.proposed += curr.totalForecast;
      existing.confidenceWeightedProposed += curr.confidenceWeightedProposed;
    }



    return acc;
  }, []);

  type DataKey = "definite" | "confidenceWeightedProposed";

  const [activeKey, setActiveKey] = useState<DataKey | null>(null);

  const handleLegendClick = (data: any) => {
    const dataKey = data.dataKey as DataKey;
    setActiveKey((prev) => (prev === dataKey ? null : dataKey));
  };

  const shouldShow = (key: DataKey) => activeKey === null || activeKey === key;

  const monthCellStyle = {
    border: "1px solid #ccc",
    p: 1,
    width: 110,          // ✅ fixed width
    minWidth: 110,
    maxWidth: 110,
    textAlign: "left",
    whiteSpace: "nowrap",
  };
  const leftCellStyle = {
    border: "1px solid #ccc",
    p: 1,
    width: 270,        // ✅ increase only left column
    minWidth: 220,
    whiteSpace: "nowrap",  // ✅ prevent wrapping
    fontWeight: 500,
  };

  const definiteKey = showDefiniteAfterTP
    ? "definiteAfterThirdParty"
    : "definite";

  const proposedKey = includeProposed
    ? "proposed"
    : "confidenceWeightedProposed";

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      {/* 🔹 Chart */}
      <div style={{ width: "100%", height: 400 }}>

        <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthName" />
              <YAxis />
              <Tooltip
                cursor={{ fill: "transparent" }}
                formatter={(value: number, name: string) => [`£${value}`, name]}
              />
              <Legend onClick={handleLegendClick} />
              {shouldShow("definite") && (
                <Bar
                  // dataKey="definite"
                  //name="Definite"
                  dataKey={definiteKey}
                  name={
                    showDefiniteAfterTP
                      ? "Definite - ThirdParty"
                      : "Definite"
                  }
                  fill="#1e66ab" // blue
                  stackId="a"   // 🔹 stack with orange
                  radius={[6, 6, 0, 0]}
                />
              )}
              {shouldShow("confidenceWeightedProposed") && (
                <Bar
                  // dataKey="confidenceWeightedProposed"
                  // name="Confidence Weighted Proposed"
                  dataKey={proposedKey}
                  name={
                    includeProposed
                      ? "Proposed"
                      : "Confidence Weighted Proposed"
                  }
                  fill="#f39c12" // orange
                  stackId="a"   // 🔹 stack with blue
                  radius={[6, 6, 0, 0]}
                />
              )}
            </BarChart>
        </ResponsiveContainer>

      </div>

      {/* 🔹 Data Grid */}
      <div style={{ marginTop: 20, overflowX: "auto" }}>
        <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={showDefiniteAfterTP}
              onChange={(e) => setShowDefiniteAfterTP(e.target.checked)}
            />
          }
          label="Include 3rd party in graph"
        />
        </Box>
        <Box>
        <FormControlLabel
            control={
              <Checkbox
                checked={includeProposed}
                onChange={(e) => setIncludeProposed(e.target.checked)}
              />
            }
            label="Include Proposed In Graph"
          />
        </Box>
        <Box
          component="table"
          sx={{ borderCollapse: "collapse", width: "100%", textAlign: "left", tableLayout: "fixed", }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={leftCellStyle} />
              {chartData.map((d) => (
                <Box
                  component="th"
                  key={d.monthName}
                  sx={monthCellStyle}
                >
                  {d.monthName}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            <Box component="tr">
              <Box component="td" sx={leftCellStyle}>
                Definite
              </Box>
              {chartData.map((d) => (
                <Box
                  component="td"
                  key={`def-${d.monthName}`}
                  sx={monthCellStyle}
                >
                  £{d.definite.toLocaleString()}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tr">
            <Box component="td" sx={leftCellStyle}>
              ThirdParty Cost
            </Box>
            {chartData.map((d) => (
              <Box
                component="td"
                key={`prop-${d.monthName}`}
                sx={monthCellStyle}
              >
                £{d.thirdPartyCost.toLocaleString()}
              </Box>
            ))}
          </Box>

          <Box component="tr">
            <Box component="td" sx={leftCellStyle}>
              Definite-ThirdParty
            </Box>
            {chartData.map((d) => (
              <Box
                component="td"
                key={`prop-${d.monthName}`}
                sx={monthCellStyle}
              >
                £{d.definiteAfterThirdParty.toLocaleString()}
              </Box>
            ))}
          </Box>

          <Box component="tr">
            <Box component="td" sx={leftCellStyle}>
              Proposed
            </Box>
            {chartData.map((d) => (
              <Box
                component="td"
                key={`prop-${d.monthName}`}
                sx={monthCellStyle}
              >
                £{d.proposed.toLocaleString()}
              </Box>
            ))}


          </Box>
          <Box component="tr">
            <Box component="td" sx={leftCellStyle}>
              Confidence Weighted Proposed
            </Box>
            {chartData.map((d) => (
              <Box
                component="td"
                key={`conf-${d.monthName}`}
                sx={monthCellStyle}
              >
                £{d.confidenceWeightedProposed.toLocaleString()}
              </Box>
            ))}
          </Box>
        </Box>
        {/* 🔹 KPI Section */}
        {totals && (
          <Box
            sx={{
              marginLeft: "auto",
              marginTop: 3,
              padding: 2,
              border: "1px solid #ccc",
              borderRadius: 1,
              maxWidth: 380,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#555", mb: 1 }}>
              {totals.monthRange
                ? `${totals.monthRange} Month Totals`
                : "Financial Year Totals"}
            </Typography>

            <Typography sx={{ fontSize: "14px", mb: 0.5 }}>
              Definite: <b>£{totals.definiteTotal.toLocaleString()}</b>
            </Typography>

            <Typography sx={{ fontSize: "14px", mb: 0.5 }}>
              Proposed: <b>£{totals.proposedTotal.toLocaleString()}</b>
            </Typography>

            <Typography
              sx={{
                fontSize: "14px",
                mb: 0.5
              }}
            >
              Confidence Weighted Proposed: <b>£{totals.confidenceTotal.toLocaleString()}</b>
            </Typography>
          </Box>
        )}


      </div>
    </div>
  );
};

export default Graph;
